const looksLikeEmail = (s) =>
  typeof s === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());

const isGenericMetaUser = (u) => !u || u === "guest" || u === "anonymous";

// Mongo ObjectId-shaped string. Used to decide whether `meta.user` is a
// resolvable user reference (post-#96 server signups carry the new user's
// Mongo _id here, not an email) so the modal can prefer a directory hit
// over the time-match fallback.
const looksLikeObjectId = (s) =>
  typeof s === "string" && /^[a-f0-9]{24}$/i.test(s.trim());

// Canonical "user creation timestamp" key on the BE response. Backend
// confirmed schema uses `created_date` (default Date.now on create); the
// other aliases are kept as forward-compat fallbacks in case BE later
// projects `createdAt`/`joinedAt` for parity with other schemas.
const USER_CREATED_KEYS = [
  "created_date",
  "createdAt",
  "created_at",
  "created",
  "dateCreated",
  "joinedAt",
  "joined",
];

const userCreatedMs = (user) => {
  if (!user || typeof user !== "object") return NaN;
  for (const key of USER_CREATED_KEYS) {
    const raw = user[key];
    if (raw == null || raw === "") continue;
    const ms = new Date(raw).getTime();
    if (Number.isFinite(ms)) return ms;
  }
  return NaN;
};

/**
 * Index users for signup drill-down enrichment. Event logs may store
 * `meta.user` as:
 *   • a generic placeholder ("guest", "anonymous") — pre-#96 or guest signup
 *   • the user's email — when FE knows the email at log time
 *   • the user's Mongo `_id` — post-#96 server-side enrichment
 *
 * We index by both email (lowercased) and `_id` so any of those shapes can
 * resolve to a name/email pair without a server round-trip. The
 * `withCreated` array is the ordered fallback used by
 * `matchSignupsToUsersByTime` when the event itself has nothing to link on.
 */
export const buildSignupUserDirectory = (users) => {
  const byEmail = new Map();
  const byId = new Map();
  const withCreated = [];

  (Array.isArray(users) ? users : []).forEach((user) => {
    const email = typeof user?.email === "string" ? user.email.trim().toLowerCase() : "";
    const id = user?._id ? String(user._id).trim() : "";
    const entry = {
      email: typeof user?.email === "string" ? user.email.trim() : "",
      name: typeof user?.name === "string" ? user.name.trim() : "",
    };
    if (email) byEmail.set(email, entry);
    if (id) byId.set(id, entry);
    // Time-match only meaningful when we can display the user — needs an email.
    if (!email) return;
    const createdMs = userCreatedMs(user);
    if (Number.isFinite(createdMs)) {
      withCreated.push({ email, name: entry.name, createdMs });
    }
  });

  return { byEmail, byId, withCreated };
};

const identityFromEventRow = (row, allRows) => {
  const detail = row?.detail || {};
  const meta = row?.meta || {};

  let email =
    (looksLikeEmail(meta.user) ? meta.user.trim() : null) ||
    (typeof detail.email === "string" ? detail.email.trim() : null) ||
    (typeof detail.userEmail === "string" ? detail.userEmail.trim() : null) ||
    (typeof meta.email === "string" ? meta.email.trim() : null) ||
    null;

  let name =
    (typeof detail.name === "string" ? detail.name.trim() : null) ||
    (typeof meta.name === "string" ? meta.name.trim() : null) ||
    null;

  if (meta.guestSessionId && Array.isArray(allRows)) {
    const peers = allRows.filter(
      (r) => r.meta?.guestSessionId === meta.guestSessionId
    );
    if (!email) {
      const withEmail = peers.find((r) => looksLikeEmail(r.meta?.user));
      if (withEmail) email = withEmail.meta.user.trim();
      else {
        const detailEmail = peers.find((r) => looksLikeEmail(r.detail?.email));
        if (detailEmail) email = detailEmail.detail.email.trim();
      }
    }
    if (!name) {
      const withName = peers.find(
        (r) =>
          (typeof r.detail?.name === "string" && r.detail.name.trim()) ||
          (typeof r.meta?.name === "string" && r.meta.name.trim())
      );
      if (withName) {
        name =
          (withName.detail?.name && String(withName.detail.name).trim()) ||
          (withName.meta?.name && String(withName.meta.name).trim()) ||
          null;
      }
    }
  }

  return { email, name };
};

/**
 * Greedy match signup events to users by created timestamp (±5 min).
 * Only used when the event row has no email on it.
 */
export const matchSignupsToUsersByTime = (signupRows, directory) => {
  const map = new Map();
  const usedEmails = new Set();
  const { withCreated } = directory;
  if (!withCreated.length) return map;

  const sorted = [...signupRows].sort(
    (a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0)
  );

  sorted.forEach((row) => {
    const eventMs = new Date(row.timestamp || 0).getTime();
    if (!Number.isFinite(eventMs)) return;

    let best = null;
    let bestDelta = Infinity;
    withCreated.forEach((candidate) => {
      if (usedEmails.has(candidate.email)) return;
      const delta = Math.abs(candidate.createdMs - eventMs);
      if (delta <= 5 * 60 * 1000 && delta < bestDelta) {
        best = candidate;
        bestDelta = delta;
      }
    });

    if (best) {
      usedEmails.add(best.email);
      const id = row._id || `${row.timestamp}-${row.meta?.guestSessionId || ""}`;
      map.set(id, { email: best.email, name: best.name || "" });
    }
  });

  return map;
};

const enrichIdentityFromDirectory = (email, name, directory) => {
  if (!email || !directory?.byEmail) return { email, name };
  const hit = directory.byEmail.get(email.toLowerCase());
  if (!hit) return { email, name };
  return {
    email: hit.email || email,
    name: name || hit.name || "",
  };
};

const identityFromDirectoryUserRef = (userRef, directory) => {
  if (!directory || !userRef) return null;
  if (looksLikeEmail(userRef)) {
    const hit = directory.byEmail?.get(String(userRef).trim().toLowerCase());
    if (hit?.email) {
      return { email: hit.email, name: hit.name || "" };
    }
  }
  if (looksLikeObjectId(userRef)) {
    const hit = directory.byId?.get(String(userRef).trim());
    if (hit?.email) {
      return { email: hit.email, name: hit.name || "" };
    }
  }
  return null;
};

const identityFromPeerRows = (row, allRows, directory) => {
  const guestSessionId = row?.meta?.guestSessionId;
  if (!guestSessionId || !Array.isArray(allRows) || !directory) return null;

  const peers = allRows.filter((r) => r.meta?.guestSessionId === guestSessionId);
  for (const peer of peers) {
    const peerUser = peer?.meta?.user;
    const fromRef = identityFromDirectoryUserRef(peerUser, directory);
    if (fromRef?.email) return fromRef;
  }
  return null;
};

export const findAnchorRowForSessionKey = (sessionKey, allRows) => {
  if (!sessionKey || !Array.isArray(allRows)) return null;
  const matches = allRows.filter(
    (r) =>
      r?.meta?.guestSessionId === sessionKey || r?.meta?.user === sessionKey
  );
  if (!matches.length) return null;
  return (
    matches.find((r) => looksLikeEmail(r.meta?.user)) ||
    matches.find(
      (r) =>
        (typeof r.detail?.name === "string" && r.detail.name.trim()) ||
        (typeof r.detail?.email === "string" && r.detail.email.trim()) ||
        (typeof r.meta?.name === "string" && r.meta.name.trim())
    ) ||
    matches[0]
  );
};

export const resolveAccountCreatedIdentity = (row, allRows, directory, timeMatchMap) => {
  let { email, name } = identityFromEventRow(row, allRows);

  // 1) Event already carries the email → enrich name from directory.
  if (email) {
    return enrichIdentityFromDirectory(email, name, directory);
  }

  // 2) meta.user looks like a Mongo _id (post-#96 server signup) → directory
  //    lookup by id recovers email + name without a network round-trip.
  const metaUser = row?.meta?.user;
  const fromMetaUser = identityFromDirectoryUserRef(metaUser, directory);
  if (fromMetaUser?.email) {
    return {
      email: fromMetaUser.email,
      name: fromMetaUser.name || name || "",
    };
  }

  // 2b) Same guest session may carry the resolvable user id on a sibling row.
  const fromPeer = identityFromPeerRows(row, allRows, directory);
  if (fromPeer?.email) {
    return {
      email: fromPeer.email,
      name: fromPeer.name || name || "",
    };
  }

  // 3) Fall back to created_date ±5 min time match.
  const rowId = row._id || `${row.timestamp}-${row.meta?.guestSessionId || ""}`;
  const timed = timeMatchMap?.get(rowId);
  if (timed?.email) {
    return {
      email: timed.email,
      name: timed.name || name || "",
    };
  }

  return { email: null, name: name || null };
};

/**
 * Resolve a display label from a session key (guestSessionId or meta.user)
 * used by engagement drill-downs where we only have the key, not a full row.
 */
export const resolveIdentityFromSessionKey = (sessionKey, allRows, directory) => {
  if (!sessionKey) {
    return { email: null, name: null, displayLabel: "Guest", isGuest: true };
  }

  const fromRef = identityFromDirectoryUserRef(sessionKey, directory);
  if (fromRef?.email || fromRef?.name) {
    const label = fromRef.name?.trim() || fromRef.email?.trim();
    return { ...fromRef, displayLabel: label, isGuest: false };
  }

  const anchor = findAnchorRowForSessionKey(sessionKey, allRows);
  if (anchor) {
    const { email, name } = resolveAccountCreatedIdentity(anchor, allRows, directory);
    if (name || email) {
      return {
        email,
        name,
        displayLabel: (name || email || "").trim(),
        isGuest: false,
      };
    }
  }

  const tail = String(sessionKey).slice(-4);
  return {
    email: null,
    name: null,
    displayLabel: tail ? `Guest · …${tail}` : "Guest",
    isGuest: true,
  };
};

/** Primary identity label for drill-down rows — name/email when known, guest only when anonymous. */
export const formatActivityIdentityLabel = ({ name, email, row, sessionKey }) => {
  const resolvedName = typeof name === "string" ? name.trim() : "";
  const resolvedEmail = typeof email === "string" ? email.trim() : "";
  if (resolvedName) return resolvedName;
  if (resolvedEmail) return resolvedEmail;

  const metaUser = row?.meta?.user;
  if (!isGenericMetaUser(metaUser) && looksLikeEmail(metaUser)) {
    return metaUser.trim();
  }

  const key = sessionKey || row?.meta?.guestSessionId || "";
  const tail = key ? String(key).slice(-4) : "";
  return tail ? `guest · …${tail}` : "guest";
};

export const rowsForAccountCreated = (row, allRows, resolvedEmail) => {
  if (!Array.isArray(allRows)) return [];
  const meta = row?.meta || {};
  const email =
    resolvedEmail ||
    (looksLikeEmail(meta.user) ? meta.user : null);

  if (email) {
    const normalized = email.trim().toLowerCase();
    return allRows.filter((r) => {
      const u = r.meta?.user;
      if (looksLikeEmail(u) && u.trim().toLowerCase() === normalized) return true;
      const dEmail = r.detail?.email;
      return (
        typeof dEmail === "string" &&
        dEmail.trim().toLowerCase() === normalized
      );
    });
  }
  if (meta.guestSessionId) {
    return allRows.filter((r) => r.meta?.guestSessionId === meta.guestSessionId);
  }
  return [row];
};

/** Display label for signup drill-down — never generic "New account" when we can be specific. */
export const accountCreatedDisplayHeadline = ({ name, email, metaUser }) => {
  if (name && String(name).trim()) return String(name).trim();
  if (email && String(email).trim()) return String(email).trim();
  if (!isGenericMetaUser(metaUser) && metaUser) return String(metaUser).trim();
  return 'Account (details pending)';
};

/** Plain-language identity for activity feed rows (name/email when logged). */
export const formatSessionIdentityLabel = (row, allRows) => {
  const { email, name } = identityFromEventRow(row, allRows);
  if (name && email) return `${name} (${email})`;
  if (name) return name;
  if (email) return email;
  const metaUser = row?.meta?.user;
  if (!isGenericMetaUser(metaUser) && looksLikeEmail(metaUser)) return metaUser.trim();
  return "Guest";
};

export { looksLikeEmail, isGenericMetaUser };
